import { useEffect, useRef } from 'react';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './admin.module.css';

interface ConfirmDeleteProps {
  title: string;
  /** Plain language about what goes and what stays (FR-062). */
  impact: string;
  confirmLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The confirmation a content deletion goes through (T101).
 *
 * Its job is to state the CONSEQUENCE, not to add friction. The sentence it
 * shows says both halves of FR-062 — the content leaves, the history does not
 * — because an administrator hesitating over "Remover" is usually asking
 * exactly that question, and the honest answer makes the decision easier
 * rather than scarier.
 *
 * Deliberately NOT a typed-confirmation dialog: that is FR-077's requirement
 * for a learner destroying their own account, which is irreversible and
 * personal. Deleting a Lesson destroys no personal record and can be
 * re-authored, so the same ceremony here would be theatre — and ceremony
 * applied where it is not warranted is what teaches people to type through it
 * where it is.
 *
 * L2, AND AN OPAQUE CORE — the same two decisions `Drawer.tsx` records, for
 * the same reasons. § 8 gives L3 to the one hero of a screen and paints it in
 * grad/brand; this dialog took that layer, so a destructive question arrived
 * on a drifting green→blue field with a red button and a transparent ghost
 * button floating on it, and the sentence explaining what is lost sat as
 * running text on the one gradient § 4 holds away from running text. A dialog
 * is L2 by § 8's own definition. And because it sits OVER the page across a
 * scrim, its ground is taken to full opacity: at L2's 92% the screen
 * underneath shows through the words somebody is being asked to weigh.
 *
 * Focus moves to the dialog on open and Escape cancels, so it is operable
 * without a pointer.
 */
export function ConfirmDelete({
  title,
  impact,
  confirmLabel,
  busy,
  onCancel,
  onConfirm,
}: ConfirmDeleteProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className={styles.confirmLayer}>
      <div className={styles.confirmPanel}>
        <Surface layer={2} radius="xl">
          <div
            ref={ref}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            className={styles.confirm}
            data-testid="confirm-delete"
          >
            <Text as="h2" id="confirm-delete-title" variant="subheading">
              {title}
            </Text>
            <Text as="p" variant="body" tone="muted">
              {impact}
            </Text>
            <div className={styles.confirmActions}>
              <span data-testid="confirm-cancel">
                {busy ? (
                  <Button id="confirm-cancel" variant="ghost" disabled disabledReason="Removendo.">
                    Cancelar
                  </Button>
                ) : (
                  <Button id="confirm-cancel" variant="ghost" onClick={onCancel}>
                    Cancelar
                  </Button>
                )}
              </span>
              <span data-testid="confirm-delete-action">
                {busy ? (
                  <Button
                    id="confirm-delete-action"
                    variant="destructive"
                    disabled
                    disabledReason="Removendo."
                  >
                    Removendo…
                  </Button>
                ) : (
                  <Button id="confirm-delete-action" variant="destructive" onClick={onConfirm}>
                    {confirmLabel}
                  </Button>
                )}
              </span>
            </div>
          </div>
        </Surface>
      </div>
    </div>
  );
}
